import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Search, 
  Edit,
  BookOpen,
  Database,
  Wrench,
  GitBranch,
  MessageSquare,
  Mail,
  PhoneForwarded,
  Code2,
  NotebookPen,
  Hash
} from 'lucide-react';

// Helper function to get brain setting display info
const getBrainSettingDisplay = (settingType, actionType) => {
  switch (settingType) {
    case 'simple_action':
      let ActionIcon = Wrench;
      let label = 'Simple Action';

      if (actionType) {
        switch (actionType) {
          case 'sms':
            ActionIcon = MessageSquare;
            label = 'SMS Action';
            break;
          case 'email':
            ActionIcon = Mail;
            label = 'Email Action';
            break;
          case 'transfer':
            ActionIcon = PhoneForwarded;
            label = 'Call Transfer';
            break;
          case 'api':
            ActionIcon = Code2;
            label = 'API Action';
            break;
          case 'note':
            ActionIcon = NotebookPen;
            label = 'Note Action';
            break;
          case 'dtmf':
            ActionIcon = Hash;
            label = 'DTMF Action';
            break;
        }
      }

      return {
        icon: ActionIcon,
        bgColor: 'bg-amber-100',
        textColor: 'text-amber-700',
        label
      };
    case 'simple_answer':
      return {
        icon: BookOpen,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        label: 'Simple Answer'
      };
    case 'complex_answer':
      return {
        icon: Database,
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-700',
        label: 'Complex Answer'
      };
    case 'complex_action':
      return {
        icon: GitBranch,
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        label: 'Complex Action'
      };
    default:
      return {
        icon: BookOpen,
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-600',
        label: 'Setting'
      };
  }
};

const UpdateExistingModal = ({ isOpen, onClose, availableSettings, inquiry, onSelectSetting }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter settings based on search query
  const filteredSettings = useMemo(() => {
    if (!searchQuery.trim()) return availableSettings;

    const query = searchQuery.toLowerCase();
    return availableSettings.filter(setting => 
      setting.name?.toLowerCase().includes(query) ||
      setting.brain_category_name?.toLowerCase().includes(query)
    );
  }, [availableSettings, searchQuery]);

  // Group settings by category
  const groupedSettings = useMemo(() => {
    const groups = {};

    filteredSettings.forEach(setting => {
      const categoryName = setting.brain_category_name || 'Uncategorized';
      if (!groups[categoryName]) {
        groups[categoryName] = [];
      }
      groups[categoryName].push(setting);
    });

    // Sort categories alphabetically, but put 'Uncategorized' last
    const sortedCategories = Object.keys(groups).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });

    return sortedCategories.map(categoryName => ({
      categoryName,
      settings: groups[categoryName].sort((a, b) => a.name.localeCompare(b.name))
    }));
  }, [filteredSettings]);

  const handleSettingSelect = (setting) => {
    onSelectSetting(setting);
    setSearchQuery(''); // Reset search when closing
  };

  const handleClose = () => {
    setSearchQuery(''); // Reset search when closing
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl mx-4 max-h-[85vh] sm:max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Update an Existing Section</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Select a section to update
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="p-4 sm:p-6 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search sections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Settings List */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {groupedSettings.length === 0 ? (
              <div className="text-center py-8 sm:py-12 px-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Edit className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                </div>
                <h3 className="text-gray-500 text-sm font-medium mb-1">
                  {searchQuery ? 'No sections match your search' : 'No sections available'}
                </h3>
                <p className="text-gray-400 text-xs">
                  {searchQuery ? 'Try adjusting your search terms' : 'Create a new section instead'}
                </p>
              </div>
            ) : (
              <div className="p-4 sm:p-6">
                {groupedSettings.map((group, groupIndex) => (
                  <div key={group.categoryName} className={groupIndex > 0 ? 'mt-6 sm:mt-8' : ''}>
                    {/* Category Header */}
                    <div className="flex items-center mb-3 sm:mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-300"></div>
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          {group.categoryName}
                        </h3>
                      </div>
                      <div className="flex-1 ml-2 sm:ml-3 border-t border-gray-200"></div>
                      <span className="ml-2 sm:ml-3 text-xs text-gray-400 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                        {group.settings.length}
                      </span>
                    </div>

                    {/* Settings in this Category */}
                    <div className="space-y-2 sm:space-y-3">
                      {group.settings.map((setting) => {
                        const display = getBrainSettingDisplay(setting.type, setting.action_type);
                        const IconComponent = display.icon;

                        return (
                          <motion.button
                            key={setting.id}
                            onClick={() => handleSettingSelect(setting)}
                            className="w-full text-left p-3 sm:p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all duration-200 group"
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="flex items-start gap-2 sm:gap-3">
                              {/* Type Icon */}
                              <div className={`p-1.5 sm:p-2 rounded-lg ${display.bgColor} ${display.textColor} flex-shrink-0`}>
                                <IconComponent className="w-3 h-3 sm:w-4 sm:h-4" />
                              </div>

                              {/* Setting Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                                  <h4 className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                    {setting.name}
                                  </h4>
                                  <span className={`text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${display.bgColor} ${display.textColor} font-medium flex-shrink-0`}>
                                    {display.label}
                                  </span>
                                </div>
                              </div>

                              {/* Arrow */}
                              <div className="flex-shrink-0 text-gray-400 group-hover:text-blue-500 transition-colors">
                                <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UpdateExistingModal; 